import { ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type Size = 'sm' | 'md' | 'lg';
type TimeValue = string | null;

@Component({
    selector: 'core-ui-input-time',
    templateUrl: './input-time.component.html',
    styleUrls: ['./input-time.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputTimeComponent),
            multi: true,
        },
    ],
})
export class InputTimeComponent implements ControlValueAccessor {
    @ViewChild('panelEl', { read: ElementRef }) panelEl?: ElementRef<HTMLElement>;
    @Input() label = '';
    @Input() placeholder = '__:__';

    @Input() icon: string | null = null;
    @Input() iconPosition: 'left' | 'right' = 'left';
    @Input() sizeIcon: 'sm' | 'md' | 'lg' = 'lg';
    @Input() iconColor = '#5F7081';

    @Input() block = false;
    @Input() size: Size = 'md';
    @Input() uiMode: 'form' | 'filter' = 'form';
    @Input() clearable = false;

    @Input() readOnly = false;

    @Input() minHour = 0;
    @Input() maxHour = 23;
    @Input() minuteStep = 1;
    @Input() minTime: TimeValue = null;
    @Input() maxTime: TimeValue = null;

    @Output() valueChange = new EventEmitter<string | null>();

    value: string | null = null;
    disabled = false;

    isOpen = false;
    selectedHour: number | null = null;
    selectedMinute: number | null = null;

    scrollStrategy: ScrollStrategy;

    positions: ConnectedPosition[] = [
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 5 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -5 },
    ];

    overlayWidth = 220;

    private ignoreNextDocDown = false;
    private static openedRef: InputTimeComponent | null = null;

    private onChange: (val: string | null) => void = () => { };
    private onTouched: () => void = () => { };

    constructor(private elementRef: ElementRef,
        private overlay: Overlay
    ) {
        this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    }

    get isFilled(): boolean {
        return this.uiMode === 'form' && !!(this.value && this.value.trim());
    }

    get displayValue(): string {
        return this.value?.trim() ? this.value : this.placeholder;
    }

    get showClearButton(): boolean {
        return this.clearable && !!this.value?.trim() && !this.disabled && !this.readOnly;
    }

    get previewValue(): string {
        const h = this.selectedHour ?? this.getHourFromValue();
        const m = this.selectedMinute ?? this.getMinuteFromValue();

        if (h == null && m == null) return '__:__';
        if (h != null && m == null) return `${this.pad2(h)}:__`;
        if (h == null && m != null) return `__: ${this.pad2(m)}`.replace(' ', '');
        return `${this.pad2(h!)}:${this.pad2(m!)}`;
    }

    get hours(): number[] {
        const start = Math.max(0, this.minHour);
        const end = Math.min(23, this.maxHour);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    get minutes(): number[] {
        const step = Math.max(1, Number(this.minuteStep) || 1);
        const out: number[] = [];
        for (let m = 0; m <= 59; m += step) out.push(m);
        return out;
    }

    writeValue(val: string | null): void {
        this.value = val;
        if (this.isOpen) this.syncLocalSelectionFromValue();
    }

    registerOnChange(fn: (val: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (isDisabled) this.isOpen = false;
    }

    toggleDropdown(): void {
        if (this.disabled || this.readOnly) return;

        const nextOpen = !this.isOpen;

        if (nextOpen) {
            if (InputTimeComponent.openedRef && InputTimeComponent.openedRef !== this) {
                InputTimeComponent.openedRef.close();
            }
            InputTimeComponent.openedRef = this;

            this.updateOverlayWidth();
            this.syncLocalSelectionFromValue();
        } else {
            if (InputTimeComponent.openedRef === this) InputTimeComponent.openedRef = null;
            this.onTouched();
        }

        this.isOpen = nextOpen;
        if (this.isOpen) this.onTouched();
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    onOverlayDetach(): void {
        if (this.isOpen) this.close();
    }

    onOverlayAttach(): void {
        requestAnimationFrame(() => this.centerSelectedOptions());
    }

    close(): void {
        this.isOpen = false;
        if (InputTimeComponent.openedRef === this) InputTimeComponent.openedRef = null;
        this.onTouched();
    }

    onSelectHour(h: number): void {
        if (this.disabled || this.readOnly) return;
        if (this.isHourDisabled(h)) return;
        this.selectedHour = h;
        if (this.selectedMinute != null && this.isMinuteDisabled(this.selectedMinute)) {
            this.selectedMinute = null;
        }
    }

    onSelectMinute(m: number): void {
        if (this.disabled || this.readOnly) return;
        if (this.isMinuteDisabled(m)) return;

        this.selectedMinute = m;

        if (this.selectedHour == null) {
            this.selectedHour = this.getHourFromValue();
        }

        if (this.selectedHour == null) return;

        this.emitAndClose();
    }

    private emitAndClose(): void {
        if (this.selectedHour == null || this.selectedMinute == null) return;
        if (!this.isTimeAllowed(this.selectedHour, this.selectedMinute)) return;

        const newValue = `${this.pad2(this.selectedHour)}:${this.pad2(this.selectedMinute)}`;

        this.value = newValue;

        this.onChange(newValue);
        this.onTouched();

        this.valueChange.emit(newValue);
        this.close();
    }

    clearValue(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled || this.readOnly) return;

        this.value = null;
        this.selectedHour = null;
        this.selectedMinute = null;
        this.close();

        this.onChange(null);
        this.valueChange.emit(null);
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.isOpen) return;
        if (this.ignoreNextDocDown) return;

        const t = ev.target as Node;

        const host = this.elementRef.nativeElement as HTMLElement;
        const panel = this.panelEl?.nativeElement;

        const clickedInOrigin = host.contains(t);
        const clickedInPanel = !!panel && panel.contains(t);

        if (!clickedInOrigin && !clickedInPanel) {
            this.close();
        }
    }

    ngAfterViewInit(): void {
        window.addEventListener('resize', this.onResize);
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this.onResize);
    }

    private onResize = () => {
        if (!this.isOpen) return;
        this.updateOverlayWidth();
    };

    private syncLocalSelectionFromValue(): void {
        this.selectedHour = this.getHourFromValue();
        this.selectedMinute = this.getMinuteFromValue();
    }

    private getHourFromValue(): number | null {
        const v = (this.value ?? '').trim();
        if (!v) return null;

        const [hh] = v.split(':');
        const n = Number(hh);
        return Number.isFinite(n) ? n : null;
    }

    private getMinuteFromValue(): number | null {
        const v = (this.value ?? '').trim();
        if (!v) return null;

        const [, mm] = v.split(':');
        const n = Number(mm);
        return Number.isFinite(n) ? n : null;
    }

    private pad2(n: number): string {
        return String(n).padStart(2, '0');
    }

    isHourDisabled(h: number): boolean {
        return this.minutes.every((m) => !this.isTimeAllowed(h, m));
    }

    isMinuteDisabled(m: number): boolean {
        const h = this.selectedHour ?? this.getHourFromValue();
        if (h == null) return false;
        return !this.isTimeAllowed(h, m);
    }

    private isTimeAllowed(hour: number, minute: number): boolean {
        const value = hour * 60 + minute;
        const min = this.parseTimeToMinutes(this.minTime);
        const max = this.parseTimeToMinutes(this.maxTime);

        if (value < Math.max(0, this.minHour) * 60) return false;
        if (value > Math.min(23, this.maxHour) * 60 + 59) return false;
        if (min != null && value < min) return false;
        if (max != null && value > max) return false;
        return true;
    }

    private parseTimeToMinutes(value: TimeValue): number | null {
        if (!value) return null;
        const match = value.match(/^(\d{1,2}):(\d{2})/);
        if (!match) return null;

        const hour = Number(match[1]);
        const minute = Number(match[2]);
        if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
        return hour * 60 + minute;
    }

    private getTriggerEl(): HTMLElement | null {
        return (this.elementRef.nativeElement as HTMLElement)
            .querySelector('.core-ui-input-time__trigger');
    }

    private updateOverlayWidth(): void {
        const trigger = this.getTriggerEl();
        const w = trigger?.getBoundingClientRect().width ?? 0;
        this.overlayWidth = Math.max(220, Math.ceil(w));
    }

    private centerSelectedOptions(): void {
        const panel = this.panelEl?.nativeElement;
        if (!panel) return;

        panel.querySelectorAll<HTMLElement>('.core-ui-input-time__list').forEach((list) => {
            const selected = list.querySelector<HTMLElement>('.core-ui-input-time__item--active');
            if (!selected) return;

            const listRect = list.getBoundingClientRect();
            const selectedRect = selected.getBoundingClientRect();
            list.scrollTop += selectedRect.top - listRect.top
                - (list.clientHeight - selected.clientHeight) / 2;
        });
    }
}
